/**
 * SimpleIngestGate7Page.jsx - Gate 7 Preview & Activation Interface
 * 
 * CRITICAL: This is part of the ISOLATED Simple Ingestion pipeline.
 * Matches the EditTitlePage layout for consistency.
 * 
 * Features:
 * - Display and EDIT manuscript metadata
 * - Audio status and stats panel
 * - Preview button opens full player interface
 * - Activate button triggers activation workflow
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { BACKEND_URL } from '@/config/api';
import { xhrFetchAuth, xhrFetchAuthPost } from '@/lib/xhrFetch';
import ActivationChargeConfirmModal from './ActivationChargeConfirmModal';
import PaymentMethodRequiredModal from './PaymentMethodRequiredModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Edit,
  Loader2,
  Eye,
  Rocket,
  BookOpen,
  Clock,
  Music,
  FileText,
  DollarSign,
  Calendar,
  Save,
  PowerOff
} from 'lucide-react';
import { getGenresForBookType, getThemesForGenre } from '@/constants/genreThemes';

const API = `${BACKEND_URL}/api`;

const SimpleIngestGate7Page = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Job and manuscript data
  const [job, setJob] = useState(null);
  const [manuscript, setManuscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Cover image
  const [coverUrl, setCoverUrl] = useState(null);
  
  // Editable form state
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    isbn: '',
    book_type: 'fiction',
    genre: '',
    theme: '',
    purchase_url: '',
    is_promotional: false,
    description: ''
  });
  const [formDirty, setFormDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Activation state
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [activationProgress, setActivationProgress] = useState([]);
  const [activationStep, setActivationStep] = useState('');
  const [activationResult, setActivationResult] = useState(null);
  
  // Payment gate state (Phase 3)
  const [showChargeConfirmModal, setShowChargeConfirmModal] = useState(false);
  const [chargeCardInfo, setChargeCardInfo] = useState({ last4: null, brand: null });
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  
  // Preview state
  const [isPreviewing, setIsPreviewing] = useState(false);
  
  // Fetch job and manuscript data
  // FIX4: Uses xhrFetchAuth to bypass rrweb interceptor that corrupts response bodies
  const fetchData = useCallback(async () => {
    if (!jobId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch job details using xhrFetchAuth
      const jobResult = await xhrFetchAuth(`${API}/simple-ingest/jobs/${jobId}`);
      if (!jobResult.ok) {
        throw new Error(jobResult.json?.message || 'Job not found');
      }
      const jobData = jobResult.json?.data || jobResult.json;
      setJob(jobData);
      
      // Check if Gate 6 is complete
      const gateOrder = {
        'GATE_1': 1, 'GATE_2': 2, 'GATE_3': 3, 
        'GATE_4': 4, 'GATE_5': 5, 'GATE_6': 6, 'GATE_7': 7
      };
      const lastGate = gateOrder[jobData.last_successful_gate] || 0;
      
      if (lastGate < 6) {
        setError(`Gate 6 (TTS Generation) must be complete before preview. Current: Gate ${lastGate}`);
        setLoading(false);
        return;
      }
      
      // If no manuscript yet, create it
      if (!jobData.manuscript_id) {
        const createResult = await xhrFetchAuth(
          `${API}/simple-ingest/jobs/${jobId}/create-manuscript`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        if (createResult.ok) {
          jobData.manuscript_id = createResult.json?.data?.manuscript_id || createResult.json?.manuscript_id;
          setJob({...jobData});
        }
      }
      
      // Fetch manuscript using Simple Ingest endpoint
      if (jobData.manuscript_id) {
        const msResult = await xhrFetchAuth(`${API}/simple-ingest/jobs/${jobId}/manuscript`);
        if (msResult.ok) {
          const msData = msResult.json?.data || msResult.json;
          setManuscript(msData);
          
          // Populate form with manuscript data
          setFormData({
            title: msData.title || '',
            subtitle: msData.subtitle || '',
            isbn: msData.isbn || '',
            book_type: msData.book_type || 'fiction',
            genre: msData.genre || '',
            theme: msData.theme || '',
            purchase_url: msData.purchase_url || '',
            is_promotional: msData.is_promotional || false,
            description: msData.description || ''
          });
        }
      }
      
      // Fetch cover image (this returns binary, so use regular fetch)
      try {
        const coverResponse = await fetch(`${API}/simple-ingest/jobs/${jobId}/cover`);
        if (coverResponse.ok && coverResponse.headers.get('content-type')?.includes('image')) {
          const coverBlob = await coverResponse.blob();
          setCoverUrl(URL.createObjectURL(coverBlob));
        }
      } catch (coverErr) {
        console.warn('[Gate7] Could not load cover:', coverErr);
      }
      
    } catch (err) {
      console.error('[Gate7] Error fetching data:', err);
      setError(err.message || 'Failed to load manuscript data');
    } finally {
      setLoading(false);
    }
  }, [jobId]);
  
  useEffect(() => {
    fetchData();
    
    return () => {
      if (coverUrl) {
        URL.revokeObjectURL(coverUrl);
      }
    };
  }, [fetchData]);
  
  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormDirty(true);
  };
  
  // Save metadata changes
  // FIX4: Uses xhrFetchAuth to bypass rrweb interceptor
  const handleSaveMetadata = async () => {
    if (!manuscript?.id || !formDirty) return;
    
    setSaving(true);
    try {
      const result = await xhrFetchAuth(`${API}/simple-ingest/jobs/${jobId}/update-metadata`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (result.ok) {
        setFormDirty(false);
        // Refresh manuscript data
        await fetchData();
      } else {
        alert(result.json?.message || 'Failed to save changes');
      }
    } catch (err) {
      console.error('[Gate7] Save error:', err);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle Preview - opens player
  // FIX4: Uses xhrFetchAuth to bypass rrweb interceptor
  const handlePreview = async () => {
    if (!manuscript?.id) {
      alert('No manuscript found. Please wait for manuscript creation.');
      return;
    }
    
    setIsPreviewing(true);
    try {
      // Use Simple Ingest preview endpoint instead of main manuscript open
      const result = await xhrFetchAuth(`${API}/simple-ingest/jobs/${jobId}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (result.ok) {
        const data = result.json;
        const sessionId = data.data?.session_id || data.session_id;
        if (sessionId) {
          navigate(`/player/${sessionId}`, { 
            state: { returnTo: `/simple-ingest/preview/${jobId}` } 
          });
        } else {
          alert('Preview session creation failed - no session ID returned');
        }
      } else {
        const errorMsg = result.json?.detail || result.json?.message || 'Failed to open preview';
        alert(errorMsg);
      }
    } catch (err) {
      console.error('[Gate7] Preview error:', err);
      alert('Failed to open preview: ' + err.message);
    } finally {
      setIsPreviewing(false);
    }
  };
  
  // Phase 3: Payment gate check before activation
  const handleActivationWithPaymentGate = async () => {
    if (!jobId) return;
    
    try {
      // Step 1: Check activation fee status
      const checkRes = await xhrFetchAuth(`${API}/payments/activation-fee-check?job_id=${jobId}`);
      
      if (!checkRes.ok) {
        alert(checkRes.json?.detail || 'Failed to check activation fee');
        return;
      }
      
      const feeCheck = checkRes.json;
      
      // No payment needed — proceed directly to activation
      if (!feeCheck.needs_payment) {
        handleActivate();
        return;
      }
      
      // Payment method required — show PaymentMethodRequiredModal
      if (feeCheck.payment_method_required) {
        setShowPaymentMethodModal(true);
        return;
      }
      
      // Payment needed, card on file — show charge confirm modal
      setChargeCardInfo({ last4: feeCheck.last4, brand: feeCheck.brand });
      setShowChargeConfirmModal(true);
      
    } catch (err) {
      console.error('[Gate7] Payment gate error:', err);
      alert('Failed to check activation fee: ' + err.message);
    }
  };
  
  // Phase 3: Handle charge confirmation
  const handleChargeConfirmed = async () => {
    setShowChargeConfirmModal(false);
    
    try {
      const chargeRes = await xhrFetchAuthPost(`${API}/payments/charge-activation-fee`, { job_id: jobId });
      
      if (!chargeRes.ok || !chargeRes.json?.success) {
        const reason = chargeRes.json?.reason;
        if (reason === 'payment_failed') {
          alert(`Payment failed — ${chargeRes.json?.error || 'Unknown error'}. Your manuscript was not activated.`);
        } else if (reason === 'payment_method_required') {
          setShowPaymentMethodModal(true);
        } else {
          alert(chargeRes.json?.detail || 'Charge failed');
        }
        return;
      }
      
      // Charge succeeded — proceed to activation
      handleActivate();
    } catch (err) {
      console.error('[Gate7] Charge error:', err);
      alert('Payment failed: ' + err.message);
    }
  };
  
  // Phase 3: After payment method added, re-trigger the gate
  const handlePaymentMethodAdded = () => {
    setShowPaymentMethodModal(false);
    handleActivationWithPaymentGate();
  };
  
  // Handle Activate - uses Simple Ingest specific activation
  // FIX4: Uses xhrFetchAuth to bypass rrweb interceptor
  const handleActivate = async () => {
    if (!manuscript?.id) {
      alert('No manuscript found');
      return;
    }
    
    setIsActivating(true);
    setActivationResult(null);
    setActivationProgress([
      { id: 'validate', label: 'Validating manuscript data...', status: 'pending' },
      { id: 'activate', label: 'Activating manuscript...', status: 'pending' },
      { id: 'summary', label: 'Generating AI description...', status: 'pending' },
      { id: 'finalize', label: 'Finalizing activation...', status: 'pending' }
    ]);
    setActivationStep('Starting activation...');
    
    try {
      // Step 1: Validate
      setActivationProgress(prev => prev.map(s => 
        s.id === 'validate' ? { ...s, status: 'active' } : s
      ));
      setActivationStep('Validating manuscript data...');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setActivationProgress(prev => prev.map(s => 
        s.id === 'validate' ? { ...s, status: 'complete' } : s
      ));
      
      // Step 2: Activate
      setActivationProgress(prev => prev.map(s => 
        s.id === 'activate' ? { ...s, status: 'active' } : s
      ));
      setActivationStep('Activating manuscript...');
      
      // Call Simple Ingest activation endpoint using xhrFetchAuth
      const result = await xhrFetchAuth(`${API}/simple-ingest/jobs/${jobId}/activate-manuscript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ generate_summary: true })
      });
      
      if (result.ok) {
        const resultData = result.json?.data || result.json;
        
        setActivationProgress(prev => prev.map(s => 
          s.id === 'activate' ? { ...s, status: 'complete' } : s
        ));
        
        // Step 3: Summary (may have been generated)
        setActivationProgress(prev => prev.map(s => 
          s.id === 'summary' ? { ...s, status: resultData.summary_generated ? 'complete' : 'skipped' } : s
        ));
        setActivationStep(resultData.summary_generated ? 'AI description generated!' : 'Skipped summary generation');
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Step 4: Finalize
        setActivationProgress(prev => prev.map(s => 
          s.id === 'finalize' ? { ...s, status: 'complete' } : s
        ));
        
        setActivationStep('Manuscript activated successfully!');
        setActivationResult({
          success: true,
          message: resultData.message || 'Manuscript is now active and discoverable!'
        });
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Refresh data
        await fetchData();
        
      } else {
        const errorMessage = result.json?.detail || result.json?.message || 'Activation failed';
        
        setActivationResult({
          success: false,
          message: errorMessage
        });
      }
    } catch (err) {
      console.error('[Gate7] Activation error:', err);
      setActivationResult({
        success: false,
        message: err.message || 'Failed to activate manuscript'
      });
    } finally {
      setIsActivating(false);
    }
  };
  
  // Handle Deactivate
  // FIX4: Uses xhrFetchAuth to bypass rrweb interceptor
  const handleDeactivate = async () => {
    if (!manuscript?.id) return;
    
    if (!confirm('Are you sure you want to deactivate this manuscript? It will no longer be visible in the library.')) {
      return;
    }
    
    try {
      const result = await xhrFetchAuth(`${API}/simple-ingest/jobs/${jobId}/deactivate-manuscript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (result.ok) {
        await fetchData();
      } else {
        alert(result.json?.message || 'Failed to deactivate manuscript');
      }
    } catch (err) {
      console.error('[Gate7] Deactivate error:', err);
      alert('Failed to deactivate manuscript');
    }
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Loading manuscript preview...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Cannot Preview</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/author-console')} className="bg-indigo-600 hover:bg-indigo-500">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Console
          </Button>
        </div>
      </div>
    );
  }
  
  // Get stats
  const gate6Data = job?.artifacts?.gate6 || {};
  const gate4Data = job?.artifacts?.gate4 || {};
  const audioStatus = gate6Data.status || 'unknown';
  const isActive = manuscript?.is_active || false;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900" data-testid="simple-ingest-gate7">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/author-console')}
            className="text-slate-300 hover:text-white"
            data-testid="back-to-console-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Console
          </Button>
          
          <div className="flex items-center gap-3">
            {formDirty && (
              <Button
                onClick={handleSaveMetadata}
                disabled={saving}
                className="bg-amber-600 hover:bg-amber-500"
                size="sm"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save Changes
              </Button>
            )}
            <span className={`px-3 py-1 text-sm rounded-full ${
              isActive 
                ? 'bg-emerald-600/20 text-emerald-400' 
                : 'bg-amber-600/20 text-amber-400'
            }`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT COLUMN - Cover & Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Cover Image Card */}
            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg">Cover Image</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="aspect-[2/3] bg-slate-800 rounded-lg overflow-hidden">
                  {coverUrl ? (
                    <img 
                      src={coverUrl} 
                      alt="Cover" 
                      className="w-full h-full object-cover"
                      data-testid="manuscript-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-16 h-16 text-slate-600" />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Audio Status Card */}
            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Music className="w-5 h-5 text-indigo-400" />
                  Audio Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status Badge */}
                <div className={`px-4 py-3 rounded-lg text-center font-medium ${
                  audioStatus === 'completed' || audioStatus === 'complete'
                    ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-700' 
                    : 'bg-amber-900/30 text-amber-400 border border-amber-700'
                }`}>
                  {audioStatus === 'completed' || audioStatus === 'complete' ? (
                    <>
                      <CheckCircle className="w-5 h-5 inline mr-2" />
                      Audio Ready
                    </>
                  ) : (
                    <>
                      <Loader2 className="w-5 h-5 inline mr-2 animate-spin" />
                      {audioStatus}
                    </>
                  )}
                </div>
                
                {/* Action Buttons */}
                {(audioStatus === 'completed' || audioStatus === 'complete') && (
                  <div className="space-y-3">
                    <Button
                      onClick={handlePreview}
                      disabled={!manuscript || isPreviewing}
                      className="w-full bg-indigo-600 hover:bg-indigo-500"
                      data-testid="preview-btn"
                    >
                      {isPreviewing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      Preview
                    </Button>
                    
                    <Button
                      variant="outline"
                      disabled
                      className="w-full border-slate-600 text-slate-400"
                      data-testid="edit-outline-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Outline (Coming Soon)
                    </Button>
                    
                    {isActive ? (
                      <Button
                        onClick={handleDeactivate}
                        className="w-full bg-red-600 hover:bg-red-500"
                        data-testid="deactivate-btn"
                      >
                        <PowerOff className="w-4 h-4 mr-2" />
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setShowActivationModal(true)}
                        disabled={!manuscript}
                        className="w-full bg-emerald-600 hover:bg-emerald-500"
                        data-testid="activate-btn"
                      >
                        <Rocket className="w-4 h-4 mr-2" />
                        Activate
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* RIGHT COLUMN - Editable Info & Stats */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title Information - EDITABLE */}
            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center justify-between">
                  <span>Title Information</span>
                  {formDirty && (
                    <span className="text-xs text-amber-400 font-normal">Unsaved changes</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-sm">Title Name *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => handleFieldChange('title', e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white mt-1"
                      placeholder="Book title"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Subtitle</Label>
                    <Input
                      value={formData.subtitle}
                      onChange={(e) => handleFieldChange('subtitle', e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white mt-1"
                      placeholder="Optional subtitle"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">ISBN</Label>
                    <Input
                      value={formData.isbn}
                      onChange={(e) => handleFieldChange('isbn', e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white mt-1"
                      placeholder="ISBN-10 or ISBN-13"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Book Type</Label>
                    <select
                      value={formData.book_type}
                      onChange={(e) => {
                        handleFieldChange('book_type', e.target.value);
                        handleFieldChange('genre', '');
                        handleFieldChange('theme', '');
                      }}
                      className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-white"
                    >
                      <option value="fiction">Fiction</option>
                      <option value="non-fiction">Non-Fiction</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Genre</Label>
                    <select
                      value={formData.genre}
                      onChange={(e) => {
                        handleFieldChange('genre', e.target.value);
                        handleFieldChange('theme', '');
                      }}
                      className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-white"
                    >
                      <option value="">Select genre</option>
                      {getGenresForBookType(formData.book_type).map(g => (
                        <option key={g.value} value={g.value}>{g.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Theme</Label>
                    <select
                      value={formData.theme}
                      onChange={(e) => handleFieldChange('theme', e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-white"
                    >
                      <option value="">Select theme</option>
                      {getThemesForGenre(formData.genre).map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">Purchase URL</Label>
                  <Input
                    value={formData.purchase_url}
                    onChange={(e) => handleFieldChange('purchase_url', e.target.value)}
                    className="bg-slate-800 border-slate-600 text-white mt-1"
                    placeholder="https://example.com/book"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    className="bg-slate-800 border-slate-600 text-white mt-1 min-h-[100px]"
                    placeholder="Book description (AI will generate if left empty during activation)"
                  />
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                  <input
                    type="checkbox"
                    id="is_promotional"
                    checked={formData.is_promotional}
                    onChange={(e) => handleFieldChange('is_promotional', e.target.checked)}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-indigo-600"
                  />
                  <label htmlFor="is_promotional" className="text-slate-300 text-sm cursor-pointer">
                    Promotional Book (Free to Read)
                  </label>
                </div>
              </CardContent>
            </Card>
            
            {/* Book Stats */}
            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg">Book Stats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-800/50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                      <FileText className="w-3 h-3" />
                      Total Words
                    </div>
                    <p className="text-2xl font-bold text-white">
                      {(gate4Data.manifest?.metadata?.total_words || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                      <BookOpen className="w-3 h-3" />
                      Segments
                    </div>
                    <p className="text-2xl font-bold text-white">
                      {gate6Data.stats?.total_segments || 0}
                    </p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                      <DollarSign className="w-3 h-3" />
                      Processing Cost
                    </div>
                    <p className="text-2xl font-bold text-emerald-400">
                      ${((gate6Data.stats?.total_cost_cents || 0) / 100).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                      <Clock className="w-3 h-3" />
                      Est. Duration
                    </div>
                    <p className="text-2xl font-bold text-white">
                      {Math.round((gate4Data.manifest?.metadata?.estimated_total_duration_seconds || 0) / 60)}m
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Book History */}
            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-indigo-400" />
                  Book History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-slate-800/50 p-4 rounded-lg">
                    <p className="text-slate-400 text-xs mb-1">Upload Date</p>
                    <p className="text-white font-medium">{formatDate(job?.created_at)}</p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg">
                    <p className="text-slate-400 text-xs mb-1">TTS Completed</p>
                    <p className="text-white font-medium">{formatDate(gate6Data.completed_at)}</p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg">
                    <p className="text-slate-400 text-xs mb-1">Status</p>
                    <p className={`font-medium ${isActive ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      {/* Activation Confirmation Modal */}
      <Dialog open={showActivationModal} onOpenChange={setShowActivationModal}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-emerald-400" />
              Activate Manuscript
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Make your manuscript discoverable in the NewAudia library.
            </DialogDescription>
          </DialogHeader>
          
          {activationResult ? (
            <div className={`p-4 rounded-lg ${
              activationResult.success 
                ? 'bg-emerald-900/30 border border-emerald-700' 
                : 'bg-red-900/30 border border-red-700'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {activationResult.success ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <span className={activationResult.success ? 'text-emerald-300' : 'text-red-300'}>
                  {activationResult.success ? 'Success!' : 'Failed'}
                </span>
              </div>
              <p className={activationResult.success ? 'text-emerald-400' : 'text-red-400'}>
                {activationResult.message}
              </p>
            </div>
          ) : isActivating ? (
            <div className="space-y-4">
              {activationProgress.map((step) => (
                <div key={step.id} className="flex items-center gap-3">
                  {step.status === 'complete' ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : step.status === 'active' ? (
                    <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
                  ) : step.status === 'skipped' ? (
                    <div className="w-5 h-5 rounded-full bg-slate-600 flex items-center justify-center text-xs text-slate-400">-</div>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-slate-600" />
                  )}
                  <span className={`text-sm ${
                    step.status === 'complete' ? 'text-emerald-400' :
                    step.status === 'active' ? 'text-indigo-300' :
                    step.status === 'skipped' ? 'text-slate-500' :
                    'text-slate-500'
                  }`}>
                    {step.label}
                  </span>
                </div>
              ))}
              {activationStep && (
                <p className="text-sm text-slate-400 mt-4">{activationStep}</p>
              )}
            </div>
          ) : (
            <div className="py-4">
              <p className="text-slate-300 mb-4">
                This will make your manuscript visible in the public library.
              </p>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Set manuscript status to active
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Generate AI description (if empty)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Enable playback for all users
                </li>
              </ul>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowActivationModal(false);
                setActivationResult(null);
                setActivationProgress([]);
              }}
              className="border-slate-600 text-slate-300"
            >
              {activationResult ? 'Close' : 'Cancel'}
            </Button>
            
            {!activationResult && !isActivating && (
              <Button
                onClick={handleActivationWithPaymentGate}
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Confirm Activation
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3: Payment gate modals */}
      <ActivationChargeConfirmModal
        isOpen={showChargeConfirmModal}
        onConfirm={handleChargeConfirmed}
        onCancel={() => setShowChargeConfirmModal(false)}
        last4={chargeCardInfo.last4}
        brand={chargeCardInfo.brand}
      />
      <PaymentMethodRequiredModal
        isOpen={showPaymentMethodModal}
        onSuccess={handlePaymentMethodAdded}
        onDismiss={() => setShowPaymentMethodModal(false)}
      />
    </div>
  );
};

export default SimpleIngestGate7Page;
